import { IconAtom2Fill18, IconMessagesFill18 } from "nucleo-ui-fill-18"

import { cn } from "@productlane/lib/src/browser"

import { toast } from "@/components/toast"

export const AGENT_SETTINGS_HREF = "/settings/ai/agent"

export type AiFirstBlocker = "live-chat" | "agent" | null

// The agent hands chats to the team through live chat, so AI first needs both.
export function aiFirstBlocker({
	isLiveChatEnabled,
	isAgentEnabled,
}: {
	isLiveChatEnabled: boolean
	isAgentEnabled: boolean
}): AiFirstBlocker {
	if (!isLiveChatEnabled) return "live-chat"
	if (!isAgentEnabled) return "agent"
	return null
}

const BLOCKED_NOTE: Record<Exclude<AiFirstBlocker, null>, string> = {
	"live-chat": "Takes effect once Live Chat is on.",
	agent: "Takes effect once the Productlane Agent is on.",
}

const SEGMENT_CLASS = "rounded-[0.45rem] px-3 py-1.5 text-xs font-medium"
const SEGMENT_ACTIVE_CLASS =
	"bg-white text-primary shadow-sm ring-1 ring-gray-400/60 dark:bg-gray-800 dark:ring-gray-700"
const SEGMENT_IDLE_CLASS = "text-secondary hover:text-primary"

function Segmented<T extends boolean>({
	options,
	value,
	onSelect,
}: {
	options: readonly { label: string; value: T }[]
	value: boolean
	onSelect: (value: T) => void
}) {
	return (
		<div className="inline-flex flex-shrink-0 rounded-lg border border-gray-300/70 bg-gray-100 p-0.5 dark:border-gray-700/40 dark:bg-gray-900">
			{options.map((option) => (
				<button
					key={option.label}
					type="button"
					aria-pressed={value === option.value}
					className={cn(
						SEGMENT_CLASS,
						value === option.value ? SEGMENT_ACTIVE_CLASS : SEGMENT_IDLE_CLASS,
					)}
					onClick={() => onSelect(option.value)}
				>
					{option.label}
				</button>
			))}
		</div>
	)
}

export function ConversationGroup({
	isGuestChatEnabled,
	onGuestChatChange,
	showSignInNote,
	isAiFirstEnabled,
	onAiFirstChange,
	isLiveChatEnabled,
	isAgentEnabled,
	onTurnOnLiveChat,
	onOpenAgentSettings,
}: {
	isGuestChatEnabled: boolean
	onGuestChatChange: (value: boolean) => void
	showSignInNote: boolean
	isAiFirstEnabled: boolean
	onAiFirstChange: (value: boolean) => void
	isLiveChatEnabled: boolean
	isAgentEnabled: boolean
	onTurnOnLiveChat: () => void
	onOpenAgentSettings: () => void
}) {
	const blocker = aiFirstBlocker({ isLiveChatEnabled, isAgentEnabled })

	const selectAiFirst = (value: boolean) => {
		if (!value || !blocker) {
			onAiFirstChange(value)
			return
		}
		if (blocker === "live-chat") {
			toast.info({
				id: "widget-ai-first-blocked",
				title: "Turn on Live Chat to start with AI",
				description: "The agent hands chats to your team through live chat.",
				icon: IconMessagesFill18,
				action: { label: "Turn on Live Chat", callback: onTurnOnLiveChat },
			})
			return
		}
		toast.info({
			id: "widget-ai-first-blocked",
			title: "Turn on the Productlane Agent to start with AI",
			description:
				"Visitors ask the agent first, so it has to answer on your widget.",
			icon: IconAtom2Fill18,
			action: { label: "Open agent settings", callback: onOpenAgentSettings },
		})
	}

	return (
		<div className="py-3" data-testid="widget-conversation-group">
			<div className="flex items-center justify-between gap-4">
				<div>
					<div className="text-sm font-medium text-primary">
						Who can start a conversation
					</div>
					<div className="mt-1 text-xs text-secondary">
						Applies to the widget and your portal.
					</div>
				</div>
				<Segmented
					options={
						[
							{ label: "Everyone", value: true },
							{ label: "Signed in only", value: false },
						] as const
					}
					value={isGuestChatEnabled}
					onSelect={onGuestChatChange}
				/>
			</div>
			{showSignInNote && (
				<div className="mt-2 text-xs text-secondary">
					Visitors on your own site cannot sign in until you set up identity
					verification, so the widget there shows no conversation options.
				</div>
			)}
			<div className="mt-3 flex items-center justify-between gap-4 border-t border-gray-200 pt-3 dark:border-gray-700/80">
				<div>
					<div className="text-sm font-medium text-primary">
						Where visitors start
					</div>
					<div className="mt-1 text-xs text-secondary">
						AI answers first, then hands off to your team.
					</div>
				</div>
				<Segmented
					options={
						[
							{ label: "Team or AI", value: false },
							{ label: "AI first", value: true },
						] as const
					}
					value={isAiFirstEnabled}
					onSelect={selectAiFirst}
				/>
			</div>
			{isAiFirstEnabled && blocker && (
				<div className="mt-2 text-xs text-secondary">
					{BLOCKED_NOTE[blocker]}
				</div>
			)}
		</div>
	)
}
